import React from "react";
import { bind } from "decko";
import { Modal } from 'react-bootstrap';

import Util from "./include/Util";
import PreviewStream from "./PreviewStream";
import EditThumbnail from "./EditThumbnail";
import EditTag from "./EditTag";
import GameLineup from "./include/GameLineup";
import GenericBlackBtn from "./GenericBlackBtn";
import Analytics from "./include/Analytics";

const filterAwayTagNames = ["highlights"]; //tags that may exist in backend but shouldnt be used by tagging interface

class TextAreaWithSoftCharacterLimit extends React.Component {
    /* Feature request - turns text area font color red when text gets longer
    than a set soft limit. Gives operators some indication of when ... might start appearing in apps
     */
    constructor (props) {
        super(props);
        this.state = {
            len: props.value.length
        }
    }

    componentWillReceiveProps (newProps, oldProps) {
        if (oldProps.value !== newProps.value) this.update(newProps.value);
    }

    @bind
    update (value) {
         this.setState({len: value.length})
    }

    render () {
        let props = Object.assign({}, this.props);
        if (this.state.len > this.props.softMaxLength) {
            props.className = props.className ? props.className + " too-long-text" : "too-long-text";
        }
        delete props.softMaxLength;
        return (
            <textarea {...props}
                onInput={(e) => {this.update(e.target.value)}}
            />
        );
    }
}

export default class EditClip extends React.Component {
    constructor (props) {
        super(props);

        const playlist = this.props.clip.playlist;

        //Create a deep copy, to preserve the original
        const tag = JSON.parse(JSON.stringify(this.props.clip.tag));

        this.state = {
            description: playlist.description,
            tag: tag,
            thumbnail: playlist.thumbnailUrl,
            fromTimestamp: playlist.fromTimestamp,
            toTimestamp: playlist.toTimestamp,
            eventTimestamp: this.props.clip.videoOffsetMillisec,
            lineup: null,
            tagProperties: null,
            tagTypes: null,
            possibleTagNames: [],
            tagErrorMsg: null,
            confirmationModal: null,
            rating: playlist.rating,
            publishDisabled: false,
        };
    }

    componentDidMount () {
        this.loadGameLineup(this.props.clip.gameId);
        this.loadTags();

        const clip = this.props.clip;
        console.log("Editing clip:", clip.toString(), "Server:", clip.server.name);
        try {
            Analytics.event("L2_edit",{
                stream: `${clip.server.name}:${clip.playlist.id}`,
                type: this.state.tag.action,
            });
        } catch (e) {}
    }

    componentWillUnmount () {
    }

    getServer () {
        return this.props.clip.server;
    }

    loadTags () {
        this.getServer().get("/tag", (data) => {

            let allTagNames = Object.keys(data.tags);
            this.setState({
                tagProperties: data.tags,
                tagTypes: data.types,
                possibleTagNames: allTagNames.filter(t => filterAwayTagNames.indexOf(t) === -1)
            });
        }, (error) => {
            console.error("Problem fetching tags: " + error);
            this.setState({
                tagErrorMsg: "Problem fetching tags from server. Try cancelling and reloading this clip"
            });
        });
    }

    loadGameLineup (gameId) {

        if (!gameId) {
            console.warn("Clip does not have a gameId. No team/player lineup loaded");
            return;
        }

        this.getServer().get("/game/" + gameId + "/players", (data) => (
            this.setState({
                lineup: GameLineup.load(data)
            })
        ), (error) => {
            console.error("Problem fetching game lineup: " + error);
            this.setState({
                tagErrorMsg: "Problem fetching lineup from server. Try cancelling and reloading this clip"
            });
        });
    }

    getDeletepromptBody () {
        /* Phase change events should have an extra warning message. Deleting a goal should also
           warn about changing the game score.

           Other events simply return null.
         */

        let tag = this.props.clip.tag;
        if (Object.prototype.toString.call(tag) === "[object Array]") {
            tag = tag[0];
        }

        if (!tag) {
            return null;
        }

        if (tag.action === "goal") {
            return (
                <Modal.Body>
                    <h4>
                        Extra note for deleting a goal event
                    </h4>
                    <p>
                        This will also change the game score, not just delete the video clip!
                    </p>
                    <p>
                        Operator in level 1 interface will be able to see the score change immediately.
                    </p>
                </Modal.Body>
            );
        }

        if (tag.action === "start phase" || tag.action === "end phase" || tag.action === "end of game") {
            return (
                <Modal.Body>
                    <h4>
                        Extra note for phase change event
                    </h4>
                    <p>
                        This will undo the phase change completely, not just delete the video clip.
                    </p>
                    <p>
                        This should only be done in case of major mistakes,
                        such as accidentally ending the game prematurely.
                    </p>
                    <p>
                        If live, the game will move to its previous state (first half / second half
                        etc).
                        Operator in level 1 interface will be able to re-do the phase change at the
                        correct time.
                    </p>
                </Modal.Body>
            );
        }
        return null;
    }

    @bind
    hideConfirmationPrompt () {
        this.setState({confirmationModal: null});
    }

    @bind
    showDeletePrompt () {
        const modal = (
            <Modal
                show
                dialogClassName="confirmation-modal"
                onHide={this.hideConfirmationPrompt}>
                <Modal.Header>
                    <Modal.Title>
                        Are you sure you wish to delete clip?
                    </Modal.Title>
                </Modal.Header>
                {this.getDeletepromptBody()}
                <Modal.Footer>
                    <button
                        className="btn btn-default pull-left"
                        onClick={this.hideConfirmationPrompt}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={this.onDelete}>
                        Delete
                    </button>
                </Modal.Footer>
            </Modal>
        );
        this.setState({confirmationModal: modal});
    }

    @bind
    showErrorMessage (msg) {
        const modal = (
            <Modal
                show
                dialogClassName="confirmation-modal"
                onHide={this.hideConfirmationPrompt}>
                <Modal.Header>
                    <Modal.Title>
                        {msg}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <button
                        className="btn btn-default pull-left"
                        onClick={this.hideConfirmationPrompt}>
                        OK
                    </button>
                </Modal.Footer>
            </Modal>
        );
        this.setState({confirmationModal: modal});
    }

    @bind
    showCancelPrompt () {
        const originalClip = this.props.clip.playlist;
        if (this.state.fromTimestamp === originalClip.fromTimestamp &&
            this.state.toTimestamp === originalClip.toTimestamp &&
            this.state.eventTimestamp === this.props.clip.videoOffsetMillisec &&
            this.state.description === originalClip.description &&
            this.state.rating === originalClip.rating &&
            JSON.stringify(this.state.tag) === JSON.stringify(this.props.clip.tag) &&
            this.state.thumbnail === originalClip.thumbnailUrl) {

            console.log("Clip has not mutated. Skipping cancel confirmation");
            this.props.onCancel(false);
            return;
        }

        const modal = (
            <Modal
                show
                dialogClassName="confirmation-modal"
                onHide={this.hideConfirmationPrompt}>
                <Modal.Header>
                    <Modal.Title>
                        You have unsaved changes, save clip first?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <button
                        className="btn btn-default pull-left"
                        onClick={this.hideConfirmationPrompt}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-success"
                        onClick={this.onPublish}>
                        Save
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={this.props.onCancel}>
                        Discard
                    </button>
                </Modal.Footer>
            </Modal>
        );
        this.setState({confirmationModal: modal});
    }

    @bind
    showClipInformationModal () {
        const clipText = JSON.stringify(this.props.clip.raw, undefined, 2);

        const modal = (
            <Modal
                show
                onHide={this.hideConfirmationPrompt}>
                <Modal.Header>
                    <Modal.Title>
                        Raw event information
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <pre style={{maxHeight: "60vh"}}>
                        {clipText}
                    </pre>
                </Modal.Body>
                <Modal.Footer className={"text-center"}>
                    <button
                        className="btn btn-default"
                        onClick={this.hideConfirmationPrompt}>
                        close
                    </button>
                </Modal.Footer>
            </Modal>
        );
        this.setState({confirmationModal: modal});
    }

    @bind
    onDelete () {

        if (this.state.publishDisabled) return;

        this.setState({publishDisabled: true});

        this.props.clip.delete(true,
            () => {
                this.publishComplete();
                this.props.onDelete();
            },
            (error) => {
                this.publishComplete();
                alert('Failed delete : ' + error);
            }
        );
    }

    isTagOwnGoal (tag) {
        if (tag.action !== "goal") return false;
        return "shot type" in tag && tag["shot type"].value === "own goal";
    }

    validate () {
        let tag = this.state.tag;
        if (!tag) {
            this.showErrorMessage("Cannot publish event without a tag");
            return false;
        }

        const attributes = this.state.tagProperties[tag.action];
        const requiredAttributes = ["team", "phase"];
        return requiredAttributes.every(attr => {
            if (attributes.find(a => a.name === attr)) {
                // The schema for this tag contains a required attribute
                if (tag[attr] === undefined) {
                    this.showErrorMessage("Cannot publish event. " +
                        "The field \"" + attr + "\" must be set");
                    return false;
                }
            }
            return true;
        });
    }

    displayPublishWarning (children) {
        const modal = (
            <Modal
                show
                dialogClassName="confirmation-modal"
                onHide={this.hideConfirmationPrompt}>
                <Modal.Header>
                    <Modal.Title>
                        Are you sure you wish to publish changes to this clip?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {children}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-default pull-left"
                        onClick={this.hideConfirmationPrompt}>
                        Cancel
                    </button>
                    <button
                        className="btn btn-danger"
                        onClick={this.onPublishConfirmed}>
                        OK - Publish
                    </button>
                </Modal.Footer>
            </Modal>
        );
        this.setState({confirmationModal: modal});
    }

    shouldDisplayPublishWarning () {

        const originalTag = this.props.clip.tag;
        const {tag, lineup} = this.state;

        if ((tag.action === "goal" || originalTag.action === "goal")  &&
            (tag.action !== originalTag.action || tag.team.id !== originalTag.team.id)) {

            // If this is, or was, a goal then we want to warn about implicit changes to the score
            // if the team or the action has changed. Relevant when fixing mistakes, annulled goals etc

            this.displayPublishWarning(
                <div>
                    <h4>
                        This change, involving a goal event, will implicitly change the score.
                    </h4>
                    <p>
                        Operator in level 1 interface will be able to see the score change immediately.
                    </p>
                </div>
            );
            return true;
        }

        if (tag.keeper || originalTag.keeper) {
            const prevKeeper = originalTag.keeper ? originalTag.keeper.id : null;
            const nextKeeper = tag.keeper ? tag.keeper.id : null;
            const prevTeam = originalTag.team ? originalTag.team.id : null;
            const nextTeam = tag.team ? tag.team.id : null;

            if (prevKeeper !== null && prevTeam === nextTeam && prevKeeper !== nextKeeper) {

                // If the operator has changed keeper, we provide an extra prompt
                // It's really easy to accidentally change keeper attribute
                const prevKeeperName = originalTag.keeper ? originalTag.keeper.value : null;
                const nextKeeperName = tag.keeper ? tag.keeper.value : null;

                this.displayPublishWarning(
                    <div>
                        <h4>
                            The keeper property has been changed, {prevKeeperName} => {nextKeeperName}
                        </h4>
                        <p>
                            Please verify that this change is correct before submitting.
                            <br />
                            If it is, please verify the lineup and/or any relevant substitution events that may have caused this error.
                            <br />
                            If an error wrt keeper has been discovered, remember to verify all other shots/goals from the match as well.
                            <br />
                            Changing keeper may affect future events.
                        </p>
                    </div>
                );
                return true;
            }
        }

        if (tag.action === "substitution") {
            /* Very easy to accidentally invert the substitution, who goes in, who goes out. Check for invalid sub */

            if (tag["player in"] === undefined || tag["player out"] === undefined) {

                this.displayPublishWarning(
                    <div>
                        <h4>
                            A player is missing from this event.
                        </h4>
                        <p>
                            We expect both players to exist when publishing a substitution.
                            If the relevant player is missing from the lineup, publish and then contact support when possible.
                            Update the event once the player is available.
                        </p>
                    </div>
                );
                return true;
            }

            const players = lineup.players[tag["team"]["value"]];
            let playerIn = null;
            let playerOut = null;

            for (let i=0; players && i<players.length; ++i) {
                if (players[i].id === tag["player in"]["id"]) {
                    playerIn = players[i];
                }
                if (players[i].id === tag["player out"]["id"]) {
                    playerOut = players[i];
                }
            }

            if (playerIn === null || playerOut === null || playerIn === playerOut) {
                this.displayPublishWarning(
                    <div>
                        <h4>
                            Player appears to be missing from lineup or set incorrectly.
                        </h4>
                        <p>
                            If this is not an error, contact support when possible - the lineup is likely wrong.
                            <br />
                            Update the event again once the player is available.
                        </p>
                    </div>
                );
                return true;
            }

            if (playerIn.position !== "SUB") {
                this.displayPublishWarning(
                    <div>
                        <h4>
                            Detected likely inverted substitution. Are you sure these players should not be swapped?
                        </h4>
                        <p>
                            Expected player coming onto the field to have position SUB, player has position {playerIn.position}.
                            <br />
                            If this is not a mistake, the lineup is likely incorrect.
                            In this case, publish and contact support when possible.
                            Update the event once the player is available.
                        </p>
                    </div>
                );
                return true;
            }
        }

        // Check if there are any players that appear to be on the wrong team
        if (lineup === null) return false;
        const invertedTeamProperties = ["offending player", "keeper"];
        for (const [key, obj] of Object.entries(tag)) {
            if (obj.type !== "player") continue;
            let team = tag.team.value; // We verify that team is present earlier in this validation
            if (invertedTeamProperties.includes(key) || (key === "scorer" && this.isTagOwnGoal(tag))) {
                team = lineup.getOppositeTeam(team);
            }
            if (lineup.getPlayer(obj.id, team) === undefined) {
                this.displayPublishWarning(
                    <div>
                        <h4>
                            Detected likely issue with players published on the wrong team
                        </h4>
                        <p>
                            The field "{key}" appears to be a player not in the current lineup.
                            Alternatively a player on the opposite team's lineup.
                            <br />
                            This can sometimes happen if the team was switched,
                            or if the lineup has since changed,
                            or if this has been changed to/from an own goal.
                            <br />
                            Please verify that the team is correctly set and that {obj.value} is the correct player.
                        </p>
                    </div>
                );

                return true;
            }
        }


        return false;
    }

    @bind
    publishComplete () {
        /* Keep the publish button locked for a brief moment longer, errors can occur with too frequent changes */
        setTimeout(() => this.setState({publishDisabled: false}), 250);
    }

    @bind
    onPublish () {

        if (!this.validate()) return;

        if (this.shouldDisplayPublishWarning()) return;

        // We don't require a confirmation for events other than goals
        this.onPublishConfirmed();
    }

    @bind
    onPublishConfirmed () {

        if (this.state.publishDisabled) return;

        this.setState({publishDisabled: true});

        let clip = this.props.clip;
        let playlist = clip.playlist;
        playlist.description = this.state.description;
        playlist.thumbnailUrl = this.state.thumbnail;
        playlist.tags = [this.state.tag];
        playlist.fromTimestamp = this.state.fromTimestamp;
        playlist.toTimestamp = this.state.toTimestamp;
        playlist.rating = this.state.rating;
        playlist.isPrivate = Util.playlistShouldBePrivate(this.state.tag.action, playlist.rating);
        clip.videoOffsetMillisec = this.state.eventTimestamp;
        clip.tag = this.state.tag;

        try {
            Analytics.event("L2_publish",{
                stream: `${clip.server.name}:${clip.playlist.id}`,
                details: `${playlist.fromTimestamp}:${clip.videoOffsetMillisec}:${playlist.toTimestamp}`,
            });
        } catch (e) {}

        clip.publish(() => {
            this.publishComplete();
            this.hideConfirmationPrompt();
            this.props.onPublish();
        }, (error) => {
            this.publishComplete();
            console.log("[ERROR] Saving new event ", error);
            this.showErrorMessage("Error occurred while saving: " + error);
        });
    }

    @bind
    onChangeThumb (thumb) {
        this.setState({thumbnail: thumb});
    }

    @bind
    onChangeInterval (fromTimestamp, eventTimestamp, toTimestamp) {
        console.log("Updated video interval", fromTimestamp, eventTimestamp, toTimestamp);
        try {
            Analytics.throttledEvent("L2_update_markers",{
                stream: `${this.props.clip.server.name}:${this.props.clip.playlist.id}`,
                details: `${fromTimestamp}:${eventTimestamp}:${toTimestamp}`,
            }, 2000);
        } catch (e) {}
        this.setState({
            fromTimestamp: fromTimestamp,
            eventTimestamp: eventTimestamp,
            toTimestamp: toTimestamp
        });
    }

    @bind
    onChangeDescription (ev) {
        this.setState({description: ev.target.value});
    }

    @bind
    onMutateEditTag (tag) {

        const description = this.getAutomaticDescription(tag, this.state.tag, this.state.description);
        const rc = Util.getEventRatingConfig(tag);
        const rating = Math.min(rc.max, Math.max(rc.min, this.state.rating));
        this.setState({tag, rating, description});

        console.log('Mutated tag', tag);
    }

    @bind
    onDeleteTag () {
        this.setState({
            tag: null,
        });
    }

    getAutomaticDescription (tag, oldTag, oldDescription) {

        let game = this.props.clip.playlist.game;
        let score = this.props.clip.score ? this.props.clip.score.slice() : null;

        // Change score if event is a goal and teams have been modified
        if (tag.action === 'goal' && score !== null) {
            let originalTag = this.props.clip.tag;
            if (originalTag) {
                let newTeam = tag.team === undefined ? undefined : tag.team.value;
                let oldTeam = originalTag.team === undefined ? undefined : originalTag.team.value;
                if (newTeam !== oldTeam) {
                    if (newTeam !== undefined) {
                        score[(newTeam === game.home_team.name) ? 0 : 1]++;
                    }
                    if (oldTeam !== undefined) {
                        score[(oldTeam === game.home_team.name) ? 0 : 1]--;
                    }
                    console.log('Updated score from', this.props.clip.score, 'to', score);
                }
            }
        }

        // Don't change description if the old description has been manually set (doesnt match the automatic title)
        if (oldDescription !== '' && oldDescription !== Util.createAutomaticTitle(oldTag, game, score)) {
            return oldDescription;
        }

        const desc = Util.createAutomaticTitle(tag, game, score);
        console.log('Changing description from', this.state.description, 'to', desc);

        return desc;
    }

    renderRating () {
        const rating = this.state.rating;
        const tag = this.state.tag;
        const {min, max} = Util.getEventRatingConfig(tag);

        let disabledStars = [];
        let enabledStars = [];
        for (let i=1; i<=5; ++i) {
            const isActive = rating >= i;
            const isDisabled = i < min || i > max;
            let star;
            if (isDisabled) {
                star = (
                    <div key={i} className={"disabled" + (isActive?" active":"")}>
                        ☆
                    </div>
                );
            } else {
                star = (
                    <div key={i}
                         className={isActive?"active":""}
                         onClick={()=>this.setState({rating: i})}
                         title={i}>
                        ☆
                    </div>
                );
            }
            // Note, the stars are rendered right-to-left to make the css work. Hence, unshift instead of push.
            if (isDisabled && isActive) {
                disabledStars.unshift(star);
            } else {
                enabledStars.unshift(star);
            }
        }

        let cls = "row text-center ratings-container";
        if (rating > 3) cls += " high-rating";

        return (
            <div className={cls}>
                <div className="rating disabled">
                    {disabledStars}
                </div>
                <div className="rating">
                    {enabledStars}
                </div>
            </div>
        );
    }

    render () {

        let tagErrorMsg = this.state.tagErrorMsg;
        let currentEditTag = null;

        const tag = this.state.tag;
        // Ensure all data is loaded
        if (tag !== null &&
            this.state.tagTypes !== null
            && this.state.tagProperties !== null
            && this.state.lineup !== null) {
            if (!("action" in tag && tag.action in this.state.tagProperties)) {
                tagErrorMsg = "Unknown tag '" + tag.action +
                    "'. Cannot update properties of this tag.";
            } else {
                let attributes = this.state.tagProperties[tag.action];
                currentEditTag = (
                    <EditTag
                        key={tag.action}
                        tag={tag}
                        tagTypes={this.state.tagTypes}
                        onMutate={this.onMutateEditTag}
                        onChangeActive={this.onMutateEditTag}
                        possibleTagNames={this.state.possibleTagNames}
                        prototypeProperties={attributes}
                        lineup={this.state.lineup}
                    />
                );
            }
        }

        if (tagErrorMsg) {
            currentEditTag = (
                <div className="text-center lead" style={{color: "red", width: "60%", marginLeft: "20%"}}>
                    <b><i>{tagErrorMsg}</i></b>
                </div>
            );
        }

        const metadataOnly = Util.playlistShouldBePrivate(tag.action, this.state.rating)

        return (
            <div className="edit-clip-container">
                <div className="col-md-12 with-border-bottom preview-stream-container">
                    <PreviewStream
                        hotkeyHandler={this.props.hotkeyHandler}
                        event={this.props.clip}
                        playlist={this.props.clip.playlist}
                        onChangeInterval={this.onChangeInterval} />
                    <GenericBlackBtn
                        style={{
                            position: "absolute",
                            left: "48px",
                            top: "4px",
                        }}
                        btnClassName="btn-xs small-info-button"
                        onClick={this.showClipInformationModal} >
                        <span className="glyphicon glyphicon-info-sign" />
                    </GenericBlackBtn>
                </div>
                <div className="col-md-12 edit-metadata-container">
                    <div className="col-md-3 edit-metadata">
                        {this.renderRating()}
                        <div className="row">
                            <TextAreaWithSoftCharacterLimit
                                softMaxLength={55}
                                className="form-control"
                                value={this.state.description}
                                placeholder="Video description. Must be filled"
                                onChange={this.onChangeDescription}
                            />
                        </div>
                        <div className="row">
                            <div className="col-md-3">
                                <div className="row edit-metadata-buttons">
                                    <button disabled={this.state.publishDisabled} className="btn btn-success"
                                            onClick={this.onPublish}>
                                        Publish
                                        {metadataOnly &&
                                            <small>
                                                {"\n(metadata)"}
                                            </small>
                                        }
                                    </button>
                                    <button className="btn btn-danger"
                                            disabled={this.props.clip.id === null}
                                            onClick={this.showDeletePrompt}>
                                        Delete
                                    </button>
                                    <button className="btn btn-default"
                                            onClick={this.showCancelPrompt}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-9">
                                <EditThumbnail
                                    fromTimestamp={this.state.fromTimestamp}
                                    toTimestamp={this.state.toTimestamp}
                                    videoAssetName={this.props.clip.playlist.videoAssetName}
                                    onChange={this.onChangeThumb}
                                    selected={this.state.thumbnail}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-9 edit-tag-container">
                        {currentEditTag}
                    </div>
                </div>
                {this.state.confirmationModal}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/js/EditClip.js